import type { Organization, User } from '../models';
import { getAuth } from '../firebase/config';
import { getApiUrl } from '../services/apiConfig';

const LOCAL_DEV_ORG_STORAGE_KEY = 'pathfinder.localDev.selectedOrgId';
const LOCAL_DEV_USER_ID_STORAGE_KEY = 'pathfinder.localDev.selectedUserId';
const LOCAL_DEV_ROLE_STORAGE_KEY = 'pathfinder.localDev.selectedRole';
const LOCAL_DEV_USER_ID = 'local-dev-user';

// Superuser emails that can impersonate any org everywhere
const SUPERUSER_EMAILS = ['admin@hunteinvest.com'];

type LocalDevRole = User['role'];

function isValidLocalDevRole(value: string | null): value is LocalDevRole {
  return value === 'admin' || value === 'member';
}

export function isSuperUserEmail(userEmail: string | null | undefined): boolean {
  if (!userEmail) {
    return false;
  }
  return SUPERUSER_EMAILS.includes(userEmail.toLowerCase());
}

export function canUseLocalImpersonation(userEmail?: string | null): boolean {
  return isSuperUserEmail(userEmail);
}

function clearStoredValues(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LOCAL_DEV_ORG_STORAGE_KEY);
  window.localStorage.removeItem(LOCAL_DEV_USER_ID_STORAGE_KEY);
  window.localStorage.removeItem(LOCAL_DEV_ROLE_STORAGE_KEY);
}

export function getStoredLocalOrganizationId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(LOCAL_DEV_ORG_STORAGE_KEY);
}

/** In-memory org id for API headers (synced from AuthProvider; avoids mismatch before localStorage reads). */
let activeDevOrgIdForApi: string | null = null;

export function setActiveDevOrgIdForApi(organizationId: string | null): void {
  activeDevOrgIdForApi = organizationId;
}

export function getActiveDevOrgIdForApi(): string | null {
  return activeDevOrgIdForApi;
}

export function setStoredLocalOrganizationId(organizationId: string | null, userEmail?: string | null): void {
  if (!canUseLocalImpersonation(userEmail) || typeof window === 'undefined') {
    return;
  }

  if (!organizationId) {
    clearStoredValues();
    return;
  }

  window.localStorage.setItem(LOCAL_DEV_ORG_STORAGE_KEY, organizationId);
}

export function getStoredLocalUserId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(LOCAL_DEV_USER_ID_STORAGE_KEY);
}

export function setStoredLocalUserId(userId: string | null, userEmail?: string | null): void {
  if (!canUseLocalImpersonation(userEmail) || typeof window === 'undefined') {
    return;
  }

  if (!userId) {
    window.localStorage.removeItem(LOCAL_DEV_USER_ID_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(LOCAL_DEV_USER_ID_STORAGE_KEY, userId);
}

export function getStoredLocalRole(): LocalDevRole | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedRole = window.localStorage.getItem(LOCAL_DEV_ROLE_STORAGE_KEY);
  return isValidLocalDevRole(storedRole) ? storedRole : null;
}

export function setStoredLocalRole(role: LocalDevRole | null, userEmail?: string | null): void {
  if (!canUseLocalImpersonation(userEmail) || typeof window === 'undefined') {
    return;
  }

  if (!role) {
    window.localStorage.removeItem(LOCAL_DEV_ROLE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(LOCAL_DEV_ROLE_STORAGE_KEY, role);
}

export function clearStoredLocalImpersonation(): void {
  clearStoredValues();
}

export function getLocalImpersonationHeaders(userEmail?: string | null): Record<string, string> | null {
  if (!canUseLocalImpersonation(userEmail)) {
    return null;
  }

  const organizationId = getActiveDevOrgIdForApi() ?? getStoredLocalOrganizationId();
  const userId = getStoredLocalUserId();
  const role = getStoredLocalRole() ?? 'admin';

  if (!organizationId) {
    return null;
  }

  // Cloud Run requires this opt-in header or apply_local_auth_bypass() skips and JWT uses the superuser's real org.
  return {
    'X-Local-Dev-Tools': '1',
    'X-Dev-Org-Id': organizationId,
    'X-Dev-User-Id': userId || LOCAL_DEV_USER_ID,
    'X-Dev-User-Role': role,
  };
}

export function isLocalImpersonationActive(): boolean {
  return !!getStoredLocalOrganizationId();
}

export async function fetchLocalOrganizations(userEmail?: string | null): Promise<Organization[]> {
  if (!canUseLocalImpersonation(userEmail)) {
    return [];
  }

  const token = await getAuth().currentUser?.getIdToken();
  const response = await fetch(`${getApiUrl()}/api/dev/organizations`, {
    headers: {
      'X-Local-Dev-Tools': '1',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unable to load local organizations' }));
    throw new Error(data.error || 'Unable to load local organizations');
  }

  const data = await response.json();
  return data.organizations as Organization[];
}

export async function fetchLocalOrganizationUsers(organizationId: string, userEmail?: string | null): Promise<User[]> {
  if (!canUseLocalImpersonation(userEmail) || !organizationId) {
    return [];
  }

  const token = await getAuth().currentUser?.getIdToken();
  const response = await fetch(`${getApiUrl()}/api/dev/organizations/${organizationId}/users`, {
    headers: {
      'X-Local-Dev-Tools': '1',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unable to load local users' }));
    throw new Error(data.error || 'Unable to load local users');
  }

  const data = await response.json();
  return data.users as User[];
}
